// Per-image room-label helpers. Pairs with the backend's two writers:
//
//   data.subjectImageRoomLabels.labels[] — the image classifier's per-photo answer
//     ({ firebaseUrl, mlsPhotoUrl, roomType, bed_visible, displayCategory }). roomType is
//     named from APPEARANCE, one photo at a time, so in a vacant house it's wrong exactly
//     where it matters: a bare breakfast nook reads "kitchen", an empty dining room reads
//     "living_area".
//
//   data.photoPlanRooms — the floor plan's answer ({ [firebaseUrl]: { planRoomType,
//     planRoomLabel } }, written by functions/coLiving/planRoomLabels.js from the co-living
//     room locator, which reads the plan and the photo together). It's a separate field
//     because the labels array above is rebuilt from scratch on every classify.
//
// The public deal projections (functions/*/publicProjection.js) already fold the second
// onto the first, so this module's merge is only needed on the surfaces that read the
// property doc directly. Everything downstream then reads ONE label shape.

const normKey = (u) => (typeof u === 'string' ? u.trim() : '');

/**
 * Fold data.photoPlanRooms onto the classifier's labels, joining on firebaseUrl.
 * Returns the labels untouched when there are no plan rooms (the common case: no floor
 * plan, or a company without co-living).
 *
 * @param {Array} labels - subjectImageRoomLabels.labels[]
 * @param {Object} planRooms - data.photoPlanRooms
 * @returns {Array} labels, each optionally carrying planRoomType / planRoomLabel
 */
export const mergePlanRooms = (labels, planRooms) => {
    if (!Array.isArray(labels) || !labels.length) return labels;
    if (!planRooms || typeof planRooms !== 'object') return labels;

    const byUrl = new Map();
    for (const [url, plan] of Object.entries(planRooms)) {
        const key = normKey(url);
        if (key && plan) byUrl.set(key, plan);
    }
    if (!byUrl.size) return labels;

    return labels.map((label) => {
        const plan = byUrl.get(normKey(label?.firebaseUrl));
        if (!plan) return label;
        return {
            ...label,
            ...(plan.planRoomType ? { planRoomType: plan.planRoomType } : {}),
            ...(plan.planRoomLabel ? { planRoomLabel: plan.planRoomLabel } : {}),
        };
    });
};

/**
 * Which gallery section a photo belongs in.
 *
 * Precedence — displayCategory, then planRoomType, then roomType:
 *   displayCategory (floor_plan / porch) is derived from dedicated classifier atoms and
 *     names a section the room_type enum has no value for at all, so it stays on top.
 *   planRoomType is the floor plan's answer, read from the plan + photo TOGETHER, so it
 *     beats a single photo's appearance whenever the locator pinned that photo.
 *   roomType is the classifier's own answer — the fallback for every photo the locator
 *     never saw (no plan, no co-living, the two reads disagreed, past the photo cap).
 *
 * Falls through to the next candidate when one isn't a section the gallery renders, and
 * lands on "other" only when nothing does.
 *
 * @param {Object} label
 * @param {Array<string>} allowedRooms - the gallery's room sections (ScrollableImages' ROOM_ORDER)
 * @returns {string}
 */
export const resolveRoomBucket = (label, allowedRooms) => {
    const allowed = Array.isArray(allowedRooms) ? allowedRooms : [];
    const candidates = [label?.displayCategory, label?.planRoomType, label?.roomType];
    for (const candidate of candidates) {
        if (candidate && allowed.includes(candidate)) return candidate;
    }
    return 'other';
};

/**
 * The display-ready badge for a single photo cell, or null for no badge.
 *
 * The floor plan's printed name ("Family Room", "Rec Room") wins — it's the most specific
 * thing we know about the photo, and it's what makes a re-bucketed photo legible ("why is
 * this in Dining?"). Falls back to the existing subRoom tag, which marks an existing
 * bedroom inside the basement group on public co-living deals.
 *
 * @param {Object} label
 * @returns {string|null}
 */
export const resolveSubRoomBadge = (label) => {
    if (label?.planRoomLabel) return label.planRoomLabel;
    if (label?.subRoom === 'bedroom') return 'Bedroom';
    return label?.subRoom || null;
};
